import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TelevisionScreenSetup from './TelevisionScreenSetup';
import LgDeviceAppEditor from '../Settings/LgDeviceAppEditor';
import { useSocket } from '../../context/SocketContext';
import { normalizeMacAddress } from '../Remote/useSharedRemoteRuntime';
import {
  confirmSiteControlPairing,
  confirmSiteControlRelocation,
  createSiteControlTarget,
  deleteSiteControlTarget,
  loadSiteControlSnapshot,
  scanSiteControlNetwork,
  sendSiteControlAction,
  startSiteControlPairing,
  testSiteControlTarget,
  updateSiteControlBridges,
  updateSiteControlTarget,
} from './siteControlApi';

const ACTIONS = [
  ['KEYCODE_DPAD_UP', 'Up'],
  ['KEYCODE_DPAD_DOWN', 'Down'],
  ['KEYCODE_DPAD_LEFT', 'Left'],
  ['KEYCODE_DPAD_RIGHT', 'Right'],
  ['KEYCODE_DPAD_CENTER', 'OK'],
  ['KEYCODE_BACK', 'Back'],
  ['KEYCODE_HOME', 'Home'],
];
const ACTION_LABELS = Object.fromEntries(ACTIONS.map(([action, label]) => [action, label]));

const MANUAL_TARGET_TYPES = {
  android: {
    protocol: 'android_tv_remote_v2',
    ports: [6466, 6467],
    candidateType: 'android_box',
    type: 'android_box',
    deviceType: 'stb',
  },
  lg_webos: {
    protocol: 'lg_webos_remote',
    ports: [3000, 3001],
    candidateType: 'lg_webos',
    type: 'lg_webos_tv',
    deviceType: 'lgtv',
  },
};

const getErrorMessage = (error, fallback = 'Request failed') => (
  error?.response?.data?.message
  || error?.response?.data?.status
  || error?.message
  || fallback
);

const formatSiteControlError = (error, fallback) => {
  const code = error?.response?.data?.status || error?.response?.data?.message || error?.message;
  if (code === 'adb_pairing_port_required') return 'Enter the pairing port shown on the TV under “Pair with a code.”';
  if (code === 'adb_connect_port_required') return 'ADB needs a connect port. Open Configure ADB and enter the port shown on the main Wireless debugging screen.';
  if (code === 'pairing_code_required') return 'Enter the six-digit code currently shown on the TV.';
  if (code === 'remote_method_setup_required') return 'The selected method needs separate setup. Existing pairing credentials have been preserved.';
  if (code === 'remote_method_unsupported') return 'The selected method is unavailable. Choose another method explicitly.';
  if (error?.response?.data?.attempt?.failure?.retryDisposition === 'no_replay_after_uncertain_delivery'
      || ['adb_operation_delivery_uncertain', 'adb_pairing_delivery_uncertain'].includes(code)) {
    return 'Delivery is uncertain: the request may have reached the TV. Check its visible state before sending another command; it has not been replayed.';
  }
  if (['adb_operation_unavailable', 'adb_operation_failed',
    'adb_pairing_unavailable', 'adb_pairing_delivery_uncertain'].includes(code)) return 'The TV did not complete the ADB request. Check the TV for an “OK to trust this network” message, clear it if present, and retry.';
  if (code === 'adb_target_not_authorized') return 'This target has not approved the Suitestream ADB identity yet. Start pairing and approve the prompt on the TV.';
  if (code === 'adb_endpoint_changed') return 'The ADB endpoint changed. Scan the network again, then save the detected endpoint before pairing or testing.';
  return getErrorMessage(error, fallback);
};

const formatDeviceLabel = (node) => {
  if (!node) return 'None';
  return `${node.name || `Device ${node.id}`} · ${node.type || 'device'}${node.localIp ? ` · ${node.localIp}` : ''}`;
};

const formatRoute = (target) => {
  const route = target?.lastRoute;
  if (!route) return 'Not routed';
  return `${route.reason || 'route'} via ${route.bridgeDeviceId || 'bridge'}${route.status ? ` · ${route.status}` : ''}`;
};

const formatScanOption = (item) => {
  const tags = [];
  if (item.existingTargetDeviceId) tags.push(`target ${item.existingTargetDeviceId}`);
  else if (item.existingDeviceId) tags.push(`device ${item.existingDeviceId}`);
  if (item.paired) tags.push('paired');
  const methods = (item.connectionMethods || []).map((method) => method.label || method.id).filter(Boolean);
  if (methods.length) tags.push(methods.join(', '));
  return `${item.ip} · ${item.name || item.type || 'Unknown'} · ${(item.ports || []).join(', ') || 'no ports'}${tags.length ? ` · ${tags.join(' · ')}` : ''}`;
};

const getTargetControlMethods = (target) => {
  if (!target) return [];
  const remoteTarget = target.integrationData?.remoteTarget || {};
  const activeProtocol = target.protocol || remoteTarget.protocol;
  const adb = target.adb || remoteTarget.adb;
  // Discovery hints must not overwrite a manually edited or recovered locator
  // when Retry persists the selected control method.
  const methods = (target.connectionMethods || remoteTarget.connectionMethods || []).map((method) =>
    adb && method.protocol === 'adb_remote' && method.authMode === adb.authMode
      ? { ...method, connectEndpoint: adb.connect, pairingEndpoint: adb.pairing } : method);
  if (adb && !methods.some((method) => method.protocol === 'adb_remote' && method.authMode === adb.authMode)) {
    methods.push({
      id: 'adb_remote',
      label: adb.authMode === 'tls' ? 'ADB · Wireless debugging' : 'ADB · Legacy',
      protocol: 'adb_remote',
      authMode: adb.authMode,
      connectEndpoint: adb.connect,
      pairingEndpoint: adb.pairing,
      requiresPairing: true,
    });
  }
  if (activeProtocol && !methods.some((method) => method.protocol === activeProtocol)) {
    methods.push({
      id: activeProtocol,
      label: activeProtocol === 'android_tv_remote_v2' ? 'Google / Android TV Remote' : activeProtocol,
      protocol: activeProtocol,
      requiresPairing: true,
    });
  }
  return methods.sort((left, right) => {
    if (left.protocol === 'android_tv_remote_v2') return -1;
    if (right.protocol === 'android_tv_remote_v2') return 1;
    return String(left.label || left.id).localeCompare(String(right.label || right.id));
  });
};

function SiteControlTab({ device, site, onEquipmentChange, onOpenDeviceRemote }) {
  const socket = useSocket();
  const siteId = site?.id || device?.siteId || device?.site_id || null;
  const [snapshot, setSnapshot] = useState(null);
  const equipmentChangeRef = useRef(onEquipmentChange);
  equipmentChangeRef.current = onEquipmentChange;
  // Pairing has its own snapshot; keep the Dashboard chooser and Screen list current too.
  const equipmentKey = snapshot ? JSON.stringify((snapshot.targets || []).map((target) => [
    target.deviceId, target.name, target.areaId, target.paired, target.pairedAt,
  ])) : null;
  useEffect(() => {
    if (equipmentKey !== null) equipmentChangeRef.current?.(siteId);
  }, [equipmentKey, siteId]);

  const [scanResults, setScanResults] = useState([]);
  const [scanBridgeDeviceId, setScanBridgeDeviceId] = useState(null);
  const [relocations, setRelocations] = useState([]);
  const [selectedScanIp, setSelectedScanIp] = useState('');
  const [selectedScanMethodId, setSelectedScanMethodId] = useState('');
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [selectedTargetMethodId, setSelectedTargetMethodId] = useState('');
  const [manualHost, setManualHost] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualMac, setManualMac] = useState('');
  const [manualTargetType, setManualTargetType] = useState('android');
  const [adbHost, setAdbHost] = useState('');
  const [adbName, setAdbName] = useState('');
  const [adbTargetId, setAdbTargetId] = useState('');
  const [adbAuthMode, setAdbAuthMode] = useState('legacy');
  const [adbConnectPort, setAdbConnectPort] = useState('5555');
  const [adbPairingPort, setAdbPairingPort] = useState('');
  const [adbSetupPairingPort, setAdbSetupPairingPort] = useState('');
  const [adbSetupPairingCode, setAdbSetupPairingCode] = useState('');
  const [adbSetupOpen, setAdbSetupOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState('');
  const [editingTargetName, setEditingTargetName] = useState('');
  const [pairingSessionId, setPairingSessionId] = useState('');
  const [pairingBridgeDeviceId, setPairingBridgeDeviceId] = useState(null);
  const [pairingCode, setPairingCode] = useState('');
  const [adbConnectedTargetId, setAdbConnectedTargetId] = useState('');
  const [adbPairingTargetId, setAdbPairingTargetId] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pendingWol, setPendingWol] = useState(null);
  const [lgAppEditor, setLgAppEditor] = useState(null);
  const actionBatchRef = useRef(null);
  const adbSetupRef = useRef(null);
  const pairingPanelRef = useRef(null);

  const bridgeNodes = snapshot?.bridgeNodes || [];
  const targets = snapshot?.targets || [];
  const selectedScan = useMemo(
    () => scanResults.find((item) => item.ip === selectedScanIp) || null,
    [scanResults, selectedScanIp]
  );
  const selectedScanMethod = useMemo(
    () => (selectedScan?.connectionMethods || []).find((method) => method.id === selectedScanMethodId)
      || (selectedScan?.connectionMethods || []).find((method) => method.protocol === selectedScan?.protocol)
      || selectedScan?.connectionMethods?.[0]
      || null,
    [selectedScan, selectedScanMethodId]
  );
  const selectedTarget = useMemo(
    () => targets.find((target) => String(target.deviceId) === String(selectedTargetId)) || targets[0] || null,
    [selectedTargetId, targets]
  );
  const targetControlMethods = useMemo(() => getTargetControlMethods(selectedTarget), [selectedTarget]);
  const selectedTargetMethod = (selectedTargetMethodId
    ? targetControlMethods.find((method) => method.id === selectedTargetMethodId)
      || { id: selectedTargetMethodId, label: `${selectedTargetMethodId} (unavailable)`, protocol: 'unavailable' }
    : null)
    || targetControlMethods.find((method) => method.protocol === 'adb_remote'
      && (!selectedTarget?.adb?.authMode || method.authMode === selectedTarget.adb.authMode))
    || targetControlMethods.find((method) => method.protocol === selectedTarget?.protocol)
    || targetControlMethods[0]
    || null;
  const selectedTargetIsLg = selectedTargetMethod?.protocol === 'lg_webos_remote';
  const selectedTargetIsSony = selectedTargetMethod?.protocol === 'sony_bravia_ip_control';
  const selectedTargetIsAdb = selectedTargetMethod?.protocol === 'adb_remote';
  const selectedTargetAdbMode = selectedTargetMethod?.authMode || selectedTarget?.adb?.authMode;
  const selectedTargetIsAdbTls = selectedTargetIsAdb && selectedTargetAdbMode === 'tls';
  const selectedTargetIsAdbLegacy = selectedTargetIsAdb && selectedTargetAdbMode === 'legacy';
  const selectedTargetMethodPaired = selectedTargetIsAdb
    ? (selectedTarget?.adbAuthorization?.paired === true
      || selectedTarget?.adbAuthorization?.trustState === 'not_required')
      && (selectedTarget?.adbAuthorization?.authMode || selectedTarget?.adb?.authMode) === selectedTargetAdbMode
    : selectedTargetMethod?.protocol === 'android_tv_remote_v2'
      ? selectedTarget?.googleRemote?.paired === true || (!selectedTarget?.googleRemote && selectedTarget?.protocol === 'android_tv_remote_v2' && selectedTarget?.paired === true)
      : selectedTargetMethod?.protocol === selectedTarget?.protocol && selectedTarget?.paired === true;
  const selectedTargetMethodReady = selectedTargetIsAdb
    ? selectedTargetMethodPaired && adbConnectedTargetId === String(selectedTarget?.deviceId)
    : selectedTargetMethodPaired;
  const appEditorScope = useMemo(() => ({ siteId, deviceId: selectedTarget?.deviceId }), [siteId, selectedTarget?.deviceId]);
  const activeAppEditorScopeRef = useRef(appEditorScope);
  activeAppEditorScopeRef.current = appEditorScope;
  useEffect(() => () => { activeAppEditorScopeRef.current = null; }, []);
  const openLgApps = (initialSetup = false, scope = appEditorScope) => {
    if (activeAppEditorScopeRef.current !== scope) return;
    setLgAppEditor({ scope, initialSetup, device: { id: selectedTarget.deviceId, name: selectedTarget.name } });
  };

  const busy = status !== 'idle';

  useEffect(() => {
    if (adbPairingTargetId !== String(selectedTarget?.deviceId) || !selectedTargetIsAdbTls) return;
    pairingPanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    pairingPanelRef.current?.querySelector('[aria-label="ADB pairing port"]')?.focus({ preventScroll: true });
  }, [adbPairingTargetId, selectedTarget?.deviceId, selectedTargetIsAdbTls]);

  const refresh = useCallback(async () => {
    if (!siteId) return;
    setStatus('loading');
    setError('');
    try {
      const data = await loadSiteControlSnapshot(siteId);
      setSnapshot(data);
      if (!selectedTargetId && data?.targets?.[0]?.deviceId) {
        setSelectedTargetId(String(data.targets[0].deviceId));
      }
      setMessage('Site Control loaded.');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load Site Control.'));
    } finally {
      setStatus('idle');
    }
  }, [selectedTargetId, siteId]);

  const flushActionBatch = useCallback(() => {
    const batch = actionBatchRef.current;
    if (!batch) return;
    if (batch.timerId) clearTimeout(batch.timerId);
    actionBatchRef.current = null;
    sendSiteControlAction(siteId, batch.targetDeviceId, {
      action: batch.action,
      repeat: batch.count,
      intervalMs: 25,
      controlMethod: batch.controlMethod,
    })
      .then((data) => {
        const suffix = batch.count > 1 ? ` x${batch.count}` : '';
        setMessage(`${data?.action || batch.action}${suffix} ${data?.status || 'sent'}.`);
      })
      .catch((err) => {
        setError(getErrorMessage(err));
      });
  }, [siteId]);

  useEffect(() => () => {
    if (actionBatchRef.current?.timerId) clearTimeout(actionBatchRef.current.timerId);
    actionBatchRef.current = null;
  }, [siteId, selectedTarget?.deviceId, selectedTargetMethodId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedTarget) return;
    const methods = getTargetControlMethods(selectedTarget);
    const selectedMethod = methods.find((method) => method.protocol === 'adb_remote'
      && (!selectedTarget.adb?.authMode || method.authMode === selectedTarget.adb.authMode));
    const methodToSelect = selectedMethod || methods.find((method) => method.protocol === selectedTarget.protocol) || methods[0];
    const saved = localStorage.getItem(`siteControlMethod:${siteId}:${selectedTarget.deviceId}`);
    setSelectedTargetMethodId(saved || methodToSelect?.id || '');
    setPairingCode('');
    setPairingSessionId('');
    setAdbConnectedTargetId('');
    setAdbPairingPort('');
  }, [selectedTarget?.deviceId]);

  useEffect(() => () => {
    if (actionBatchRef.current?.timerId) {
      clearTimeout(actionBatchRef.current.timerId);
    }
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const handler = (payload) => {
      if (!payload || payload.type !== 'wake_on_lan_response') return;
      if (!pendingWol || Number(payload.deviceId) !== Number(pendingWol.bridgeDeviceId)) return;
      if (String(payload.requestId || '') !== pendingWol.requestId) return;
      const targetLabel = pendingWol.targetName || pendingWol.mac;
      if (payload.success) {
        setMessage(`Wake-on-LAN magic packet sent to ${targetLabel} (${pendingWol.mac}).`);
        setError('');
      } else {
        setError(`Wake-on-LAN failed for ${targetLabel}: ${payload.error || 'unknown error'}.`);
      }
      setPendingWol(null);
    };
    socket.on('deviceCommandResponse', handler);
    return () => socket.off('deviceCommandResponse', handler);
  }, [pendingWol, socket]);

  const run = async (nextStatus, operation, successMessage) => {
    setStatus(nextStatus);
    setError('');
    try {
      const result = await operation();
      if (successMessage) setMessage(successMessage(result));
      return result;
    } catch (err) {
      setError(formatSiteControlError(err));
      return null;
    } finally {
      setStatus('idle');
    }
  };

  const handleBridgeChange = async (field, value) => {
    if (!snapshot?.site) return;
    const primaryBridgeDeviceId = field === 'primary'
      ? value || null
      : snapshot.site.primaryBridgeDeviceId || null;
    const secondaryBridgeDeviceId = field === 'secondary'
      ? value || null
      : snapshot.site.secondaryBridgeDeviceId || null;
    const result = await run(
      'saving-bridges',
      () => updateSiteControlBridges(siteId, {
        primaryBridgeDeviceId,
        secondaryBridgeDeviceId,
      }),
      () => 'Bridge roles saved.'
    );
    if (result) setSnapshot(result);
  };

  const handleScan = async () => {
    const result = await run(
      'scanning-network',
      () => scanSiteControlNetwork(siteId, {}),
      (data) => `Found ${data?.devices?.length || 0} device(s) using ${data?.bridge?.name || `relay ${data?.bridgeDeviceId}`}.`
    );
    if (!result) return;
    const devices = Array.isArray(result.devices) ? result.devices : [];
    setScanBridgeDeviceId(result.bridgeDeviceId || null);
    setScanResults(devices);
    setRelocations(Array.isArray(result.relocations) ? result.relocations : []);
    if ((result.relocations || []).some((entry) => entry.status === 'rebound')) await refresh();
    const firstCandidate = devices.find((item) => item.canCreatePlaceholder) || devices[0] || null;
    setSelectedScanIp(firstCandidate?.ip || '');
    setSelectedScanMethodId(firstCandidate?.connectionMethods?.find((method) => method.protocol === firstCandidate.protocol)?.id
      || firstCandidate?.connectionMethods?.[0]?.id || '');
  };

  const buildScanTargetPayload = (candidate, method = null) => {
    if (!candidate || !method) return candidate;
    // Save ADB as an additional method without replacing the target's existing
    // Android TV Remote pairing and active protocol.
    const result = { ...candidate, protocol: candidate.protocol || method.protocol };
    const adbMethod = (candidate.connectionMethods || []).find((item) => item.protocol === 'adb_remote');
    const adbMethodToSave = method.protocol === 'adb_remote' ? method : adbMethod;
    if (!adbMethodToSave) return result;
    const connect = adbMethodToSave.connectEndpoint;
    if (!connect?.host || !connect?.port) return method.protocol === 'adb_remote' ? null : result;
    return {
      ...result,
      adb: {
        version: 1,
        authMode: adbMethodToSave.authMode || 'legacy',
        connect: { host: connect.host, port: Number(connect.port) },
        // The Wireless debugging pairing port is transient and is collected in
        // the pairing step. Keep the discovered value only as scan evidence.
        pairing: null,
      },
    };
  };

  const handleConfirmRelocation = async (relocation) => {
    const result = await run(
      'relocating-target',
      () => confirmSiteControlRelocation(siteId, relocation.targetDeviceId, {
        candidate: relocation.candidate,
      }),
      (data) => `Updated ${data?.target?.name || relocation.targetName} to ${relocation.candidateHost}.`
    );
    if (!result) return;
    setRelocations((current) => current.filter((entry) => entry !== relocation));
    await refresh();
  };

  const handleSaveScanTarget = async () => {
    if (!selectedScan) return;
    const scanResult = buildScanTargetPayload(selectedScan, selectedScanMethod);
    if (!scanResult) {
      setError('The ADB connection endpoint is missing. Configure its connection port before saving.');
      return;
    }
    const selectedMethod = selectedScanMethod;
    const selectedAdbMethod = selectedMethod?.protocol === 'adb_remote' ? selectedMethod : null;
    let savedTargetId = '';
    if (selectedScan.existingTargetDeviceId) {
      const existingTarget = targets.find((target) => String(target.deviceId) === String(selectedScan.existingTargetDeviceId));
      if (existingTarget) {
        const result = await run(
          'saving-target',
          () => updateSiteControlTarget(siteId, existingTarget.deviceId, {
            name: scanResult.name || existingTarget.name,
            deviceType: existingTarget.type || 'stb',
            controlOnly: true,
            scanResult,
          }),
          (data) => `Updated ${data?.target?.name || existingTarget.name} with the discovered control methods.`
        );
        if (!result) return;
        savedTargetId = String(result.target?.deviceId || existingTarget.deviceId);
        await refresh();
      } else {
        savedTargetId = String(selectedScan.existingTargetDeviceId);
      }
    } else {
      const result = await run(
        'saving-target',
        () => createSiteControlTarget(siteId, {
          scanResult,
          name: scanResult.name || scanResult.ip,
          deviceType: scanResult.suggestedTargetType || 'stb',
          controlOnly: true,
          bridgeDeviceId: scanBridgeDeviceId || snapshot?.site?.primaryBridgeDeviceId || null,
        }),
        (data) => `Saved ${data?.target?.name || selectedScan.ip}.`
      );
      if (!result) return;
      savedTargetId = String(result.target?.deviceId || '');
      await refresh();
    }
    if (selectedAdbMethod) {
      setAdbPairingPort(String(selectedAdbMethod.pairingEndpoint?.port || ''));
    }
    setSelectedTargetMethodId(selectedMethod?.id || '');
    setSelectedTargetId(savedTargetId);
    if (selectedAdbMethod?.authMode === 'tls' && savedTargetId) {
      await connectSavedAdb(savedTargetId);
    } else if (selectedAdbMethod?.authMode === 'legacy' && savedTargetId) {
      const connection = await run(
        'connecting',
        () => startSiteControlPairing(siteId, savedTargetId, {
          bridgeDeviceId: scanBridgeDeviceId || snapshot?.site?.primaryBridgeDeviceId || null,
          controlMethod: 'adb_remote',
        }),
        (data) => data?.status === 'paired'
          ? 'ADB connected. Select ADB in the control panel and test navigation below.'
          : 'Connection is waiting for TV approval. Clear “OK to trust this network” on the TV, then retry ADB below.'
      );
      if (connection?.status === 'paired') setAdbConnectedTargetId(savedTargetId);
    }
  };

  const handleAddManualTarget = async () => {
    const host = manualHost.trim();
    if (!host) {
      setError('IP address or host is required.');
      return;
    }
    const targetType = MANUAL_TARGET_TYPES[manualTargetType] || MANUAL_TARGET_TYPES.android;
    const enteredMac = normalizeMacAddress(manualMac);
    if (manualMac.trim() && !enteredMac) {
      setError('MAC address must contain 12 hexadecimal digits, for example AA:BB:CC:DD:EE:FF.');
      return;
    }
    const result = await run(
      'resolving-target',
      async () => {
        let resolvedMac = enteredMac;
        if (!resolvedMac) {
          try {
            const scan = await scanSiteControlNetwork(siteId, {
              bridgeDeviceId: snapshot?.site?.primaryBridgeDeviceId || null,
            });
            const match = (scan?.devices || []).find((item) => (
              String(item.ip || '').trim().toLowerCase() === host.toLowerCase()
            ));
            resolvedMac = normalizeMacAddress(match?.mac);
          } catch (_error) {
            // MAC discovery is best effort; adding a remote target does not require it.
          }
        }
        const created = await createSiteControlTarget(siteId, {
          scanResult: {
            ip: host,
            name: manualName.trim() || host,
            mac: resolvedMac || null,
            protocol: targetType.protocol,
            ports: targetType.ports,
            candidateType: targetType.candidateType,
            type: targetType.type,
            requiresPairing: true,
            discovery: ['manual'],
            capabilities: ['remote_keys', 'app_control', 'power'],
          },
          name: manualName.trim() || host,
          deviceType: targetType.deviceType,
          controlOnly: true,
          bridgeDeviceId: snapshot?.site?.primaryBridgeDeviceId || null,
        });
        return { ...created, resolvedMac };
      },
      (data) => data?.resolvedMac
        ? `Saved ${data?.target?.name || host} with MAC ${data.resolvedMac}.`
        : `Saved ${data?.target?.name || host}, but its MAC address was not found. Remove and re-add it with the MAC from the TV settings to test WOL.`
    );
    if (!result) return;
    setManualHost('');
    setManualName('');
    setManualMac('');
    await refresh();
    setSelectedTargetId(String(result.target?.deviceId || ''));
  };

  const startAdbTargetConfiguration = (target = null) => {
    setAdbSetupOpen(true);
    setAdbTargetId(target ? String(target.deviceId) : '');
    setAdbHost(target?.adb?.connect?.host || target?.host || '');
    setAdbName(target?.name || target?.host || '');
    const authMode = target ? target.adb?.authMode || 'legacy' : 'tls';
    setAdbAuthMode(authMode);
    setAdbConnectPort(String(target?.adb?.connect?.port || (authMode === 'tls' ? '' : 5555)));
    setAdbSetupPairingPort('');
    setAdbSetupPairingCode('');
    adbSetupRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setError('');
  };

  const startAdbConfigurationFromScan = () => {
    if (!selectedScan) return;
    setAdbSetupOpen(true);
    const target = selectedScan.existingTargetDeviceId
      ? targets.find((item) => String(item.deviceId) === String(selectedScan.existingTargetDeviceId)) || null
      : null;
    const adbMethod = (selectedScan.connectionMethods || []).find((method) => method.protocol === 'adb_remote');
    setAdbTargetId(target ? String(target.deviceId) : '');
    setAdbHost(adbMethod?.connectEndpoint?.host || selectedScan.ip || target?.host || '');
    setAdbName(selectedScan.name || target?.name || selectedScan.ip || '');
    setAdbAuthMode(adbMethod?.authMode || target?.adb?.authMode || 'tls');
    setAdbConnectPort(String(adbMethod?.connectEndpoint?.port || target?.adb?.connect?.port || ''));
    setAdbSetupPairingPort(String(adbMethod?.pairingEndpoint?.port || ''));
    setAdbSetupPairingCode('');
    adbSetupRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setError('');
  };

  const handleSaveAdbTarget = async (pairImmediately = false) => {
    const host = adbHost.trim();
    const connectPort = adbAuthMode === 'tls' && !adbConnectPort.trim() ? null : Number(adbConnectPort);
    if (!host) {
      setError('Enter the Android device’s IP address.');
      return;
    }
    if (connectPort !== null && (!Number.isInteger(connectPort) || connectPort < 1 || connectPort > 65535)) {
      setError('Enter a valid ADB connect port.');
      return;
    }
    if (pairImmediately && (!Number.isInteger(Number(adbSetupPairingPort)) || Number(adbSetupPairingPort) < 1 || Number(adbSetupPairingPort) > 65535)) {
      setError('Enter the pairing port currently shown on the TV under “Pair with a code.”');
      return;
    }
    if (pairImmediately && !/^\d{6}$/.test(adbSetupPairingCode)) {
      setError('Enter the six-digit code currently shown on the TV.');
      return;
    }
    const target = targets.find((item) => String(item.deviceId) === String(adbTargetId));
    const endpoint = { host, port: connectPort };
    const scanResult = {
      ip: host,
      name: adbName.trim() || host,
      type: 'android_box',
      candidateType: 'android_box',
      protocol: 'adb_remote',
      ports: connectPort ? [connectPort] : [],
      capabilities: ['remote_keys', 'app_control', 'screenshot'],
      discovery: ['manual'],
      requiresPairing: true,
      adb: {
        version: 1,
        authMode: adbAuthMode,
        connect: endpoint,
        pairing: null,
      },
    };
    const result = await run(
      'saving-target',
      () => target
        ? updateSiteControlTarget(siteId, target.deviceId, {
          name: scanResult.name,
          deviceType: target.type || 'stb',
          controlOnly: true,
          scanResult,
        })
        : createSiteControlTarget(siteId, {
          scanResult,
          name: scanResult.name,
          deviceType: 'stb',
          controlOnly: true,
        }),
      (data) => `${target ? 'Updated' : 'Saved'} ${data?.target?.name || host} as an ADB target.`
    );
    if (!result) return;
    const targetId = String(result.target?.deviceId || target?.deviceId || '');
    setAdbTargetId(targetId);
    if (adbAuthMode === 'tls') {
      const methodId = getTargetControlMethods(result.target).find((method) => method.protocol === 'adb_remote' && method.authMode === 'tls')?.id || 'adb_remote';
      localStorage.setItem(`siteControlMethod:${siteId}:${targetId}`, methodId);
      setSelectedTargetMethodId(methodId);
    }
    await refresh();
    setSelectedTargetId(targetId);
    if (pairImmediately) {
      await pairAndConnectAdb(targetId, {
        host: result.target?.adb?.connect?.host || host, connectPort,
        pairingPort: adbSetupPairingPort, code: adbSetupPairingCode,
      });
    } else if (adbAuthMode === 'tls') await connectSavedAdb(targetId);
  };

  const openAdbPairing = (target) => {
    const method = getTargetControlMethods(target).find((item) => item.protocol === 'adb_remote' && item.authMode === 'tls');
    if (!method) return;
    localStorage.setItem(`siteControlMethod:${siteId}:${target.deviceId}`, method.id);
    setSelectedTargetId(String(target.deviceId));
    setSelectedTargetMethodId(method.id);
    setPairingSessionId('');
    setPairingCode('');
    setAdbPairingPort('');
    setAdbPairingTargetId(String(target.deviceId));
    setError('');
  };

  const pairAndConnectAdb = async (targetId, { host, connectPort, pairingPort, code }) => {
    const result = await run('confirming', () => confirmSiteControlPairing(siteId, targetId, {
      bridgeDeviceId: snapshot?.site?.primaryBridgeDeviceId || null,
      controlMethod: 'adb_remote', code, pairingPort: Number(pairingPort),
    }));
    if (!result) return;
    setPairingCode('');
    setPairingSessionId('');
    setAdbSetupPairingCode('');
    setAdbSetupPairingPort('');
    setAdbPairingPort('');
    setAdbPairingTargetId('');
    setAdbConnectedTargetId('');
    await refresh();
    // A never-paired device may advertise its connect endpoint only after trust
    // is established. Accept only one TLS endpoint at the entered IP.
    if (!connectPort) {
      const discovered = await run('scanning-network', async () => {
        const scan = await scanSiteControlNetwork(siteId, {});
        const endpoints = (scan?.devices || []).filter((item) => item.ip === host)
          .flatMap((item) => item.connectionMethods || [])
          .filter((method) => method.protocol === 'adb_remote' && method.authMode === 'tls'
            && method.connectEndpoint?.host === host && method.connectEndpoint?.port)
          .map((method) => Number(method.connectEndpoint.port));
        const ports = [...new Set(endpoints)];
        if (ports.length !== 1) return null;
        await updateSiteControlTarget(siteId, targetId, {
          scanResult: { adb: { connect: { host, port: ports[0] } } },
        });
        return ports[0];
      });
      if (!discovered) {
        await refresh();
        setMessage('ADB paired. The connect port is not visible yet. Open Configure ADB, enter the port from the main Wireless debugging screen, and save to connect.');
        return;
      }
      setAdbConnectPort(String(discovered));
      await refresh();
    }
    const connected = await run('connecting', () => testSiteControlTarget(siteId, targetId, {
      action: 'ADB_TEST_CONNECTION', controlMethod: 'adb_remote',
    }));
    if (connected) {
      await refresh();
      setAdbConnectedTargetId(String(targetId));
      setAdbSetupOpen(false);
      setMessage('ADB paired and connected. Test navigation using the controls below.');
    } else setMessage('ADB paired. Connection could not be confirmed; check Wireless debugging and choose Connect / Retry ADB.');
  };

  const startEditingTarget = (target) => {
    setEditingTargetId(String(target.deviceId));
    setEditingTargetName(target.name || target.host || '');
  };

  const handleSaveTargetName = async () => {
    if (!editingTargetId) return;
    const result = await run(
      'saving-target',
      () => updateSiteControlTarget(siteId, editingTargetId, {
        name: editingTargetName.trim(),
      }),
      (data) => `Renamed ${data?.target?.name || 'target'}.`
    );
    if (!result) return;
    setEditingTargetId('');
    setEditingTargetName('');
    await refresh();
    setSelectedTargetId(String(result.target?.deviceId || editingTargetId));
  };

  const handleDeleteTarget = async (target) => {
    const result = await run(
      'deleting-target',
      () => deleteSiteControlTarget(siteId, target.deviceId),
      () => 'Target removed.'
    );
    if (!result) return;
    setSelectedTargetId('');
    await refresh();
  };

  const persistSelectedAdbMethod = async () => {
    if (!selectedTarget || !selectedTargetIsAdb) return true;
    const method = selectedTargetMethod;
    const endpoint = method?.connectEndpoint || selectedTarget.adb?.connect;
    const authMode = method?.authMode || selectedTarget.adb?.authMode;
    if (!endpoint?.host || (!endpoint?.port && !(authMode === 'tls' && endpoint?.port === null)) || !['legacy', 'tls'].includes(authMode)) {
      setError('The selected ADB method is missing its connection endpoint. Scan again or enter it under Manual ADB setup.');
      return false;
    }
    if (selectedTarget.adb?.authMode === authMode
        && selectedTarget.adb?.connect?.host === endpoint.host
        && Number(selectedTarget.adb?.connect?.port) === Number(endpoint.port)) return true;
    const remoteTarget = selectedTarget.integrationData?.remoteTarget || {};
    const saved = await run(
      'saving-target',
      () => updateSiteControlTarget(siteId, selectedTarget.deviceId, {
        name: selectedTarget.name,
        deviceType: selectedTarget.type || 'stb',
        controlOnly: true,
        scanResult: {
          ...remoteTarget,
          ip: endpoint.host,
          name: selectedTarget.name || endpoint.host,
          protocol: selectedTarget.protocol || remoteTarget.protocol,
          connectionMethods: selectedTarget.connectionMethods || remoteTarget.connectionMethods || [],
          adb: { version: 1, authMode, connect: { host: endpoint.host, port: endpoint.port === null ? null : Number(endpoint.port) }, pairing: null },
        },
      }),
      () => `Saved the ${authMode === 'legacy' ? 'legacy' : 'Wireless debugging'} ADB endpoint.`
    );
    if (!saved) return false;
    await refresh();
    return true;
  };

  const connectSavedAdb = async (targetId) => {
    if (!targetId) return;
    setAdbConnectedTargetId('');
    setAdbPairingTargetId('');
    const result = await run('connecting', () => startSiteControlPairing(siteId, targetId, {
      bridgeDeviceId: snapshot?.site?.primaryBridgeDeviceId || null, controlMethod: 'adb_remote',
    }), (data) => data?.status === 'paired'
      ? 'ADB connected using the existing site identity.'
      : 'Could not reconnect. Retry the connection or enter a fresh pairing port and code below.');
    if (result?.status === 'paired') {
      await refresh();
      setAdbConnectedTargetId(String(targetId));
      setMessage('ADB connected using the existing site identity.');
    } else setAdbPairingTargetId(String(targetId));
    return result;
  };

  const handleStartPairing = async () => {
    if (!selectedTarget) return;
    const method = selectedTargetMethod;
    if (!method) return;
    const scope = appEditorScope;
    const firstLgPairing = selectedTargetIsLg && !selectedTargetMethodPaired;
    if (selectedTargetIsAdb && selectedTargetMethodPaired) {
      await handleTest();
      return;
    }
    if (selectedTargetIsAdb && !(await persistSelectedAdbMethod())) return;
    setPairingSessionId('');
    setPairingCode('');
    setPairingBridgeDeviceId(null);
    const result = await run(
      'pairing',
      () => startSiteControlPairing(siteId, selectedTarget.deviceId, {
          bridgeDeviceId: snapshot?.site?.primaryBridgeDeviceId || null,
          controlMethod: selectedTargetIsAdb ? 'adb_remote' : method.protocol,
        }),
      (data) => selectedTargetIsAdbLegacy
        ? data?.status === 'paired'
          ? 'ADB connected. Select ADB in the control panel and test navigation below.'
          : 'ADB could not connect yet. Check the TV for “OK to trust this network,” approve it, then select Connect / Retry ADB.'
        : selectedTargetIsAdbTls
          ? 'Enter the pairing port and six-digit code shown on the TV, then choose Pair & connect.'
          : 'Pairing started.'
    );
    if (result && selectedTargetIsAdbLegacy && result.status === 'paired') {
      setAdbConnectedTargetId(String(selectedTarget.deviceId));
    }
    if (result?.sessionId) {
      setPairingSessionId(result.sessionId);
      setPairingBridgeDeviceId(result.bridgeDeviceId || snapshot?.site?.primaryBridgeDeviceId || null);
      if (method.protocol === 'android_tv_remote_v2') {
        await refresh();
        setMessage('Pairing started. Enter the new code shown on the TV.');
      }
    }
    if (result?.status === 'paired') {
      await refresh();
      if (selectedTargetIsLg) setMessage(result.screenSetup?.status === 'incomplete'
        ? `TV paired. Screen setup needs review: ${result.screenSetup.message || result.screenSetup.code}`
        : 'TV paired and saved. Its Screen is ready in Virtual Matrix; select the TV above or open its remote below.');
      if (firstLgPairing) openLgApps(true, scope);
    }
  };

  const handleConfirmPairing = async () => {
    if (!selectedTarget) return;
    const scope = appEditorScope;
    const firstLgPairing = selectedTargetIsLg && !selectedTargetMethodPaired;
    if (selectedTargetIsAdbTls && !(await persistSelectedAdbMethod())) return;
    if (selectedTargetIsAdbTls && (!Number.isInteger(Number(adbPairingPort)) || Number(adbPairingPort) < 1 || Number(adbPairingPort) > 65535)) {
      setError('Enter the pairing port currently shown on the TV under “Pair with a code.”');
      return;
    }
    if (selectedTargetIsAdbTls) {
      const endpoint = selectedTargetMethod?.connectEndpoint || selectedTarget.adb.connect;
      await pairAndConnectAdb(selectedTarget.deviceId, {
        host: endpoint.host, connectPort: endpoint.port,
        pairingPort: adbPairingPort, code: pairingCode,
      });
      return;
    }
    // The relay consumes an ATV session on every confirmation attempt, including failures.
    const sessionId = pairingSessionId;
    if (selectedTargetMethod?.protocol === 'android_tv_remote_v2') {
      if (!sessionId) return;
      setPairingSessionId('');
      setPairingCode('');
    }
    const result = await run(
      'confirming',
      () => confirmSiteControlPairing(siteId, selectedTarget.deviceId, {
        bridgeDeviceId: pairingBridgeDeviceId || snapshot?.site?.primaryBridgeDeviceId || null,
        sessionId,
        code: pairingCode,
        controlMethod: selectedTargetIsAdb ? 'adb_remote' : selectedTargetMethod?.protocol,
      }),
      () => 'Pairing confirmed.'
    );
    if (!result) {
      if (selectedTargetMethod?.protocol === 'android_tv_remote_v2') {
        setMessage('Choose Start Pairing again and enter the new code shown on the TV.');
      }
      return;
    }
    setPairingCode('');
    setPairingSessionId('');
    setAdbPairingTargetId('');
    await refresh();
    if (firstLgPairing && result.status === 'paired') openLgApps(true, scope);
  };

  const handleTest = async () => {
    if (!selectedTarget) return;
    const result = await run(
      'testing',
      async () => {
        return testSiteControlTarget(siteId, selectedTarget.deviceId, {
          action: selectedTargetIsAdb ? 'ADB_TEST_CONNECTION' : 'KEYCODE_DPAD_DOWN',
          controlMethod: selectedTargetMethod?.protocol,
        });
      },
      (data) => {
        if (selectedTargetIsAdb) setAdbConnectedTargetId(String(selectedTarget.deviceId));
        return selectedTargetIsAdb ? 'ADB connection verified.' : `Test ${data?.status || 'sent'} via ${data?.bridgeDeviceId || 'bridge'}.`;
      }
    );
    if (result) await refresh();
  };


  const handleWake = (target) => {
    const mac = normalizeMacAddress(target?.mac);
    if (!mac) {
      setError(`No MAC address is saved for ${target?.name || target?.host || 'this target'}. Scan it again so SuiteStream can learn its MAC address.`);
      return;
    }
    const bridgeDeviceId = snapshot?.site?.primaryBridgeDeviceId || bridgeNodes.find((node) => node.bridgeCapable)?.id || null;
    if (!socket || !bridgeDeviceId) {
      setError('No connected bridge is available to send Wake-on-LAN.');
      return;
    }
    const requestId = String(Date.now());
    socket.emit('sendCommand', {
      deviceId: bridgeDeviceId,
      command: 'wake_on_lan',
      value: { mac },
      id: Number(requestId),
    });
    setError('');
    setPendingWol({
      requestId,
      bridgeDeviceId,
      mac,
      targetName: target.name || target.host || null,
    });
    setMessage(`Sending Wake-on-LAN magic packet to ${target.name || target.host || mac} (${mac})…`);
  };

  const handleAction = async (action) => {
    if (!selectedTarget) return;
    setError('');
    const existing = actionBatchRef.current;
    if (
      existing
      && existing.action === action
      && existing.targetDeviceId === selectedTarget.deviceId
      && existing.controlMethod === selectedTargetMethod?.protocol
      && existing.count < (selectedTargetIsAdb ? 10 : 20)
    ) {
      existing.count += 1;
      clearTimeout(existing.timerId);
      existing.timerId = setTimeout(flushActionBatch, 45);
      setMessage(`${ACTION_LABELS[action] || action} x${existing.count} queued.`);
      return;
    }
    if (existing) flushActionBatch();
    actionBatchRef.current = {
      action,
      targetDeviceId: selectedTarget.deviceId,
      count: 1,
      controlMethod: selectedTargetIsAdb ? 'adb_remote' : selectedTargetMethod?.protocol,
      timerId: setTimeout(flushActionBatch, 45),
    };
    setMessage(`${ACTION_LABELS[action] || action} queued.`);
  };

  if (!siteId) {
    return <div className="dashboard-network-empty">Select a site device to use Site Control.</div>;
  }

  return (
    <div className="site-control site-control-pairing">
      <div className="site-control-header">
        <div>
          <h3>Site Control</h3>
          <p>{message || 'Manage bridge nodes and LAN remote targets for this site.'}</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}>
          Refresh
        </button>
      </div>

      {error && <div className="site-control-alert" role="alert">{error}</div>}

      <section className="site-control-section site-control-relay-summary">
        <div>
          <strong>Automatic relay selection</strong>
          <p>Commands use any healthy compatible device. Preferences are optional and never prevent safe fallback.</p>
        </div>
        <span className="site-control-relay-summary__status">Recommended</span>
      </section>

      <details className="site-control-section site-control-advanced">
        <summary>Advanced relay preferences</summary>
        <p>Use these only to favour a known reliable device. They do not lock control to that relay.</p>
        <div className="site-control-bridge-grid">
          <div className="site-control-bridge-role">
            <span>Preferred relay</span>
            <div className="site-control-bridge-controls">
              <select
                aria-label="Preferred relay"
                value={snapshot?.site?.primaryBridgeDeviceId || ''}
                onChange={(event) => handleBridgeChange('primary', event.target.value)}
                disabled={busy}
              >
                <option value="">Automatic</option>
                {bridgeNodes.filter((node) => node.bridgeCapable).map((node) => (
                  <option key={node.id} value={node.id}>{formatDeviceLabel(node)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="site-control-bridge-role">
            <span>Backup preference</span>
            <div className="site-control-bridge-controls">
              <select
                aria-label="Backup relay preference"
                value={snapshot?.site?.secondaryBridgeDeviceId || ''}
                onChange={(event) => handleBridgeChange('secondary', event.target.value)}
                disabled={busy}
              >
                <option value="">Automatic</option>
                {bridgeNodes.filter((node) => node.bridgeCapable).map((node) => (
                  <option key={node.id} value={node.id}>{formatDeviceLabel(node)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </details>

      <section className="site-control-section">
        <div className="site-control-section-title">
          <div><h4>1. Find a device</h4><p className="site-control-help">Scan the relay’s network, or enter an IP below if the device is not discovered.</p></div>
          <button type="button" className="primary" onClick={handleScan} disabled={busy}>
            {status === 'scanning-network' ? 'Scanning…' : 'Scan network'}
          </button>
        </div>
        <div className="site-control-discovery-row">
          <select value={selectedScanIp} onChange={(event) => {
            const candidate = scanResults.find((item) => item.ip === event.target.value);
            setSelectedScanIp(event.target.value);
            setSelectedScanMethodId(candidate?.connectionMethods?.find((method) => method.protocol === candidate.protocol)?.id
              || candidate?.connectionMethods?.[0]?.id || '');
          }} disabled={busy || !scanResults.length}>
            <option value="">Select a scan result</option>
            {scanResults.map((item) => (
              <option key={item.ip} value={item.ip}>
                {formatScanOption(item)}
              </option>
            ))}
          </select>
          {selectedScan?.connectionMethods?.length > 0 && (
            <select aria-label="Discovered control method" value={selectedScanMethod?.id || ''} onChange={(event) => setSelectedScanMethodId(event.target.value)} disabled={busy}>
              {selectedScan.connectionMethods.map((method) => (
                <option key={method.id} value={method.id}>{method.label || method.id}</option>
              ))}
            </select>
          )}
          <button type="button" onClick={handleSaveScanTarget} disabled={busy || !selectedScan || (selectedScanMethod?.protocol === 'adb_remote' && !buildScanTargetPayload(selectedScan, selectedScanMethod))}>
            {selectedScanMethod?.protocol === 'adb_remote'
              ? selectedScanMethod.authMode === 'legacy'
                ? 'Save & connect ADB'
                : selectedScan.existingTargetDeviceId ? 'Save ADB endpoint' : 'Save ADB target'
              : selectedScan?.existingTargetDeviceId ? 'Select Target' : 'Save Target'}
          </button>
        </div>
        {selectedScan && (
          <div className="dashboard-network-empty">
            {selectedScan.existingTargetDeviceId
              ? `Already saved as ${selectedScan.existingTargetName || `target ${selectedScan.existingTargetDeviceId}`}.`
              : selectedScan.existingDeviceId
                ? `Matches existing app device ${selectedScan.existingDeviceName || selectedScan.existingDeviceId}. Saving will enable it as a remote target.`
                : selectedScan.canCreatePlaceholder
                  ? 'Saving will create a control-only placeholder target.'
                  : 'This scan result is not eligible for a remote target.'}
          </div>
        )}
        {selectedScan && (
          <div className="site-control-discovery-row">
            <button type="button" className="secondary" onClick={startAdbConfigurationFromScan} disabled={busy}>
              {selectedScan.existingTargetDeviceId ? 'Configure this target for ADB' : 'Configure ADB / Pair manually'}
            </button>
            {selectedScan.connectionMethods?.some((method) => method.protocol === 'adb_remote')
              ? <span className="site-control-help">ADB endpoint detected. Saving keeps the target’s existing Android TV Remote pairing; port 5555 is tested immediately and can be retried after approving the TV prompt.</span>
              : <span className="site-control-help">No ADB endpoint detected. A device that has never been paired may not appear in scans. Open Wireless debugging → Pair with a code and enter its details in ADB setup.</span>}
          </div>
        )}
        {relocations.map((relocation) => (
          <div className="site-control-discovery-row" key={`${relocation.targetDeviceId}-${relocation.candidateHost}`}>
            <div className="dashboard-network-empty">
              {relocation.status === 'rebound'
                ? `${relocation.targetName} moved from ${relocation.previousHost} to ${relocation.candidateHost}; its confirmed identity and control endpoint matched.`
                : relocation.strength === 'current_locator_reachable'
                  ? `${relocation.targetName} is still reachable at ${relocation.previousHost}. The duplicate address ${relocation.candidateHost} was ignored.`
                  : relocation.strength === 'ambiguous'
                  ? `Possible addresses for ${relocation.targetName} are ambiguous. Nothing was changed.`
                  : `${relocation.targetName} may have moved from ${relocation.previousHost} to ${relocation.candidateHost}. Confirm after checking the device name and address.`}
            </div>
            {relocation.canConfirm && (
              <button type="button" onClick={() => handleConfirmRelocation(relocation)} disabled={busy}>
                Test evidence and use address
              </button>
            )}
          </div>
        ))}
        <details className="site-control-manual-remote">
          <summary>Add another remote by IP</summary>
          <div className="site-control-discovery-row">
            <input
              type="text"
              value={manualHost}
              placeholder="IP address or host"
              onChange={(event) => setManualHost(event.target.value)}
              disabled={busy}
            />
            <input
              type="text"
              value={manualName}
              placeholder="Display name"
              onChange={(event) => setManualName(event.target.value)}
              disabled={busy}
            />
            <input
              type="text"
              value={manualMac}
              placeholder="MAC (auto-detected if blank)"
              onChange={(event) => setManualMac(event.target.value)}
              disabled={busy}
            />
            <select
              value={manualTargetType}
              onChange={(event) => setManualTargetType(event.target.value)}
              disabled={busy}
              aria-label="Manual target type"
            >
              <option value="android">Android / Google TV</option>
              <option value="lg_webos">LG webOS TV</option>
            </select>
            <button type="button" onClick={handleAddManualTarget} disabled={busy || !manualHost.trim()}>
              {status === 'resolving-target' ? 'Finding MAC...' : 'Add by IP'}
            </button>
          </div>
        </details>
      </section>

      <details ref={adbSetupRef} className="site-control-section site-control-adb-setup" open={adbSetupOpen} onToggle={(event) => setAdbSetupOpen(event.currentTarget.open)}>
        <summary className="site-control-section-title">
          <h4>2. Configure ADB</h4>
        </summary>
        <div className="site-control-section-title">
          <button type="button" className="secondary" onClick={() => startAdbTargetConfiguration()} disabled={busy}>
            New ADB target
          </button>
        </div>
        <p className="site-control-help">Add a device even if the scan found nothing. For Wireless debugging, enter the pairing details below to save and pair in one step.</p>
        {targets.length > 0 && (
          <div className="site-control-discovery-row">
            <select
              aria-label="Existing target to configure for ADB"
              value={adbTargetId}
              onChange={(event) => startAdbTargetConfiguration(targets.find((target) => String(target.deviceId) === event.target.value) || null)}
              disabled={busy}
            >
              <option value="">Create a new remote target</option>
              {targets.map((target) => <option key={target.deviceId} value={target.deviceId}>{target.name || target.host}</option>)}
            </select>
          </div>
        )}
        <div className="site-control-form-grid">
          <label className="site-control-field"><span>Device IP address</span>
            <input aria-label="ADB target IP address" type="text" value={adbHost} placeholder="TV IP address"
              onChange={(event) => setAdbHost(event.target.value)} disabled={busy} />
          </label>
          <label className="site-control-field"><span>Display name</span>
            <input aria-label="ADB target name" type="text" value={adbName} placeholder="Display name"
              onChange={(event) => setAdbName(event.target.value)} disabled={busy} />
          </label>
          <label className="site-control-field"><span>Connection type</span>
            <select aria-label="ADB authentication mode" value={adbAuthMode} onChange={(event) => {
              const mode = event.target.value;
              setAdbAuthMode(mode);
              setAdbConnectPort(mode === 'legacy' ? '5555' : '');
              setAdbSetupPairingPort('');
              setAdbSetupPairingCode('');
            }} disabled={busy}>
              <option value="legacy">Legacy ADB (TCP 5555)</option>
              <option value="tls">Wireless debugging (TLS)</option>
            </select>
          </label>
          <label className="site-control-field"><span>Connect port {adbAuthMode === 'tls' && <small>Optional until paired</small>}</span>
            <input aria-label="ADB connect port" type="number" min="1" max="65535" value={adbConnectPort}
              placeholder="Connect port" onChange={(event) => setAdbConnectPort(event.target.value)} disabled={busy} />
          </label>
        </div>
        {adbAuthMode === 'tls' && (
          <div className="site-control-pairing-card">
            <div><h5>Pair with a code</h5><p>On the Android device, open <strong>Wireless debugging → Pair with a code</strong>. Keep that screen open while pairing.</p></div>
            <div className="site-control-form-grid">
              <label className="site-control-field"><span>Pairing port</span>
                <input aria-label="ADB setup pairing port" type="number" min="1" max="65535" inputMode="numeric"
                  value={adbSetupPairingPort} placeholder="e.g. 37123" onChange={(event) => setAdbSetupPairingPort(event.target.value)} disabled={busy} />
              </label>
              <label className="site-control-field"><span>Six-digit pairing code</span>
                <input aria-label="ADB setup pairing code" type="text" inputMode="numeric" maxLength="6" autoComplete="off"
                  value={adbSetupPairingCode} placeholder="000000" onChange={(event) => setAdbSetupPairingCode(event.target.value.replace(/\D/g, '').slice(0, 6))} disabled={busy} />
              </label>
            </div>
            <p className="site-control-help">The pairing port differs from the connect port on the main Wireless debugging screen. If the connect port is hidden, leave it blank; Suitestream will look for it after pairing.</p>
          </div>
        )}
        <div className="site-control-form-actions">
          {adbAuthMode === 'tls' && <button type="button" className="primary" onClick={() => handleSaveAdbTarget(true)}
            disabled={busy || !adbHost.trim() || !adbSetupPairingPort.trim() || adbSetupPairingCode.length !== 6}>
            {busy ? 'Working…' : 'Save & pair ADB'}
          </button>}
          <button type="button" className={adbAuthMode === 'tls' ? 'secondary' : 'primary'} onClick={() => handleSaveAdbTarget()} disabled={busy || !adbHost.trim()}>
            {adbTargetId ? 'Update ADB target' : 'Add ADB target'}
          </button>
        </div>
      </details>

      <section className="site-control-section">
        <div className="site-control-section-title">
          <div><h4>3. Saved devices</h4><p className="site-control-help">Configure connection details, or repair pairing with a fresh port and code.</p></div>
        </div>
        {targets.length ? (
          <div className="dashboard-network-table-wrapper">
            <table className="dashboard-network-table site-control-target-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Host</th>
                  <th>Paired</th>
                  <th>Route</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr key={target.deviceId} className={String(target.deviceId) === String(selectedTarget?.deviceId) ? 'active' : ''}>
                    <td>
                      {String(editingTargetId) === String(target.deviceId) ? (
                        <input
                          type="text"
                          value={editingTargetName}
                          onChange={(event) => setEditingTargetName(event.target.value)}
                          disabled={busy}
                        />
                      ) : (
                        target.name || `Target ${target.deviceId}`
                      )}
                    </td>
                    <td data-label="Host">{target.host || 'unknown'}</td>
                    <td data-label="Pairing">{target.adb ? <div className="site-control-method-status">
                      <span>TV Remote: {target.googleRemote?.paired || (target.protocol === 'android_tv_remote_v2' && target.paired) ? 'Yes' : 'No'}</span>
                      <span>ADB: {target.adbAuthorization?.paired ? 'Yes' : 'No'}</span>
                    </div> : target.paired ? 'Yes' : 'No'}</td>
                    <td data-label="Route">{formatRoute(target)}</td>
                    <td data-label="Error">{target.lastError || '-'}</td>
                    <td className="site-control-target-actions">
                      <button type="button" onClick={() => setSelectedTargetId(String(target.deviceId))} disabled={busy}>
                        Select
                      </button>
                      <button type="button" onClick={() => startAdbTargetConfiguration(target)} disabled={busy}>
                        Configure ADB
                      </button>
                      {getTargetControlMethods(target).some((method) => method.protocol === 'adb_remote' && method.authMode === 'tls') && (
                        <button type="button" onClick={() => openAdbPairing(target)} disabled={busy}>(re)pair ADB</button>
                      )}
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => handleWake(target)}
                        disabled={busy || Boolean(pendingWol)}
                        title={normalizeMacAddress(target.mac) ? `Send Wake-on-LAN to ${target.mac}` : 'No MAC saved; scan this target again to learn it'}
                      >
                        {pendingWol?.mac === normalizeMacAddress(target.mac) ? 'Waking…' : 'Test WOL'}
                      </button>
                      {String(editingTargetId) === String(target.deviceId) ? (
                        <button type="button" onClick={handleSaveTargetName} disabled={busy || !editingTargetName.trim()}>
                          Save
                        </button>
                      ) : (
                        <button type="button" onClick={() => startEditingTarget(target)} disabled={busy}>
                          Rename
                        </button>
                      )}
                      <button type="button" className="secondary danger" onClick={() => handleDeleteTarget(target)} disabled={busy}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dashboard-network-empty">No remote targets saved yet.</div>
        )}
      </section>

      <TelevisionScreenSetup targets={targets} siteId={siteId} onEquipmentChange={onEquipmentChange} onOpenDeviceRemote={onOpenDeviceRemote} />
      {selectedTarget && (
        <section ref={pairingPanelRef} className="site-control-section site-control-target-panel">
          <div className="site-control-section-title">
            <h4>{selectedTarget.name || selectedTarget.host}</h4>
          </div>
          <div className="site-control-pairing-row">
            {(targetControlMethods.length > 1 || selectedTargetMethod?.protocol === 'unavailable') && (
              <select aria-label="Target control method" value={selectedTargetMethod?.id || ''}
                onChange={(event) => {
                  localStorage.setItem(`siteControlMethod:${siteId}:${selectedTarget.deviceId}`, event.target.value);
                  setSelectedTargetMethodId(event.target.value);
                  setPairingSessionId('');
                  setPairingCode('');
                  const method = targetControlMethods.find((item) => item.id === event.target.value);
                  setAdbPairingPort(String(method?.pairingEndpoint?.port || ''));
                }} disabled={busy}>
                {selectedTargetMethod?.protocol === 'unavailable' && <option value={selectedTargetMethod.id} disabled>{selectedTargetMethod.label}</option>}
                {targetControlMethods.map((method) => <option key={method.id} value={method.id}>{method.label || method.id}</option>)}
              </select>
            )}
            {selectedTargetIsAdbTls ? (
              <>
                <button type="button" className="primary" onClick={async () => {
                  if (await persistSelectedAdbMethod()) await connectSavedAdb(String(selectedTarget.deviceId));
                }} disabled={busy}>
                  {status === 'connecting' ? 'Connecting…' : 'Connect / Retry ADB'}
                </button>
                <button type="button" onClick={() => openAdbPairing(selectedTarget)} disabled={busy}>
                  Pair again
                </button>
              </>
            ) : selectedTargetIsAdb ? (
              <button type="button" className="primary" onClick={selectedTargetMethodPaired ? handleTest : handleStartPairing} disabled={busy}>
                {status === 'connecting' || status === 'testing' || status === 'pairing' ? 'Connecting…' : 'Connect / Retry ADB'}
              </button>
            ) : (
              <button type="button" className="primary" onClick={handleStartPairing} disabled={busy}>
                {status === 'pairing' ? 'Pairing...' : selectedTargetIsLg ? 'Pair LG TV' : selectedTargetIsSony ? 'Pair Sony TV' : 'Start Pairing'}
              </button>
            )}
            {selectedTargetIsAdbTls && adbPairingTargetId === String(selectedTarget.deviceId) && (
              <div className="site-control-pairing-card site-control-pairing-card--repair">
                <div><h5>Pair ADB · {selectedTarget.host || selectedTarget.adb?.connect?.host}</h5>
                  <p>Open <strong>Wireless debugging → Pair with a code</strong> on this device. Enter only the fresh pairing port and code.</p></div>
                <div className="site-control-form-grid">
                  <label className="site-control-field"><span>Pairing port</span>
                    <input type="number" min="1" max="65535" aria-label="ADB pairing port" inputMode="numeric"
                      value={adbPairingPort} placeholder="Pairing port on device" onChange={(event) => setAdbPairingPort(event.target.value)} disabled={busy} />
                  </label>
                  <label className="site-control-field"><span>Six-digit pairing code</span>
                    <input type="text" inputMode="numeric" aria-label="ADB pairing code" maxLength="6"
                      value={pairingCode} placeholder="6-digit code on device" onChange={(event) => setPairingCode(event.target.value.replace(/\D/g, '').slice(0, 6))} disabled={busy} />
                  </label>
                </div>
                <div className="site-control-form-actions">
                  <button type="button" className="primary" onClick={handleConfirmPairing} disabled={busy || pairingCode.length !== 6 || !adbPairingPort.trim()}>
                    {status === 'confirming' || status === 'connecting' ? 'Pairing & connecting…' : 'Pair & connect ADB'}
                  </button>
                  <button type="button" onClick={() => { setAdbPairingTargetId(''); setPairingCode(''); setAdbPairingPort(''); }} disabled={busy}>Cancel</button>
                </div>
              </div>
            )}
            {!selectedTargetIsLg && !selectedTargetIsAdbLegacy && !selectedTargetIsAdbTls && (
              <>
                <input
                  type="text"
                  inputMode={selectedTargetIsSony ? 'text' : 'numeric'}
                  value={pairingCode}
                  placeholder={selectedTargetIsSony ? 'Sony pre-shared key' : 'Pairing code'}
                  onChange={(event) => setPairingCode(event.target.value)}
                  disabled={busy || (!pairingSessionId && !selectedTargetIsAdbTls)}
                />
                <button type="button" onClick={handleConfirmPairing} disabled={busy || (!pairingSessionId && !selectedTargetIsAdbTls) || !pairingCode.trim()}>
                  Confirm
                </button>
              </>
            )}
            {!selectedTargetIsAdb && <button type="button" onClick={handleTest} disabled={busy || !selectedTargetMethodPaired}>
              Test
            </button>}
            {selectedTargetIsLg && selectedTargetMethodPaired && <button type="button" onClick={() => openLgApps()} disabled={busy}>
              Manage TV Apps
            </button>}
          </div>
          {selectedTargetIsAdbTls && <p className="site-control-help">Suitestream first tries the saved connect port using the existing site identity. If it cannot reconnect, retry or open Wireless debugging → Pair with a code on the Android device and enter a fresh pairing port and code. The connect port and pairing port are different.</p>}
          {selectedTargetIsAdbLegacy && <p className="site-control-help">Legacy ADB tries the detected IP:5555 directly. If the TV asks “OK to trust this network,” approve it and retry. No pairing code is needed.</p>}
          <div className="site-control-action-grid">
            {ACTIONS.map(([action, label]) => (
              <button
                key={action}
                type="button"
                onPointerDown={(event) => {
                  event.preventDefault();
                  handleAction(action);
                }}
                onClick={(event) => {
                  if (event.detail === 0) handleAction(action);
                }}
                disabled={!selectedTargetMethodReady}
              >
                {label}
              </button>
            ))}
          </div>
        </section>
      )}
      {lgAppEditor?.scope === appEditorScope && <LgDeviceAppEditor
        key={`${siteId}:${lgAppEditor.device.id}`} device={lgAppEditor.device} initialSetup={lgAppEditor.initialSetup}
        onSaved={() => setMessage(`App selection saved for ${lgAppEditor.device.name || 'LG TV'}. You can edit it in Settings → Devices → Edit Device.`)}
        onClose={() => setLgAppEditor(null)} />}
    </div>
  );
}

export default SiteControlTab;
